// =====================================================
// SERVIÇO DE GERAÇÃO DE ARQUIVOS PARA GPT
// GPT decide quando gerar arquivos (CSV, PDF, JSON, etc)
// =====================================================

import { jsPDF } from 'jspdf';

export interface GeneratedFile {
  filename: string;
  content: string; // base64
  contentType: string;
  size: number;
}

class FileGenerationService {
  /**
   * Define a tool de geração de arquivos para o GPT
   */
  getFileGenerationTool() {
    return {
      type: 'function',
      function: {
        name: 'generate_file',
        description: `🚨 FERRAMENTA PARA GERAR ARQUIVOS - Gera arquivos para download.

FORMATOS SUPORTADOS:
- PDF (documentos formatados - recomendado para relatórios)
- CSV (planilhas/dados tabulares)
- JSON (dados estruturados)
- HTML (relatórios visuais)
- TXT (texto simples)
- Markdown (documentação)

QUANDO USAR:
- Usuário pede: "pdf", "documento", "relatório", "análise" → PDF
- Usuário pede: "planilha", "csv", "excel", "tabela" → CSV
- Usuário pede: "dados", "json" → JSON
- Usuário pede: "página web", "html" → HTML

FORMATO DOS DADOS:

PDF (para documentos/relatórios):
- type: "pdf"
- title: "Título do Documento"
- content: "Conteúdo em texto (suporta markdown básico: # título, ## subtítulo, - lista)"
- filename: "documento_nome.pdf"

CSV (para planilhas):
- type: "csv"
- data: [{coluna1: valor1, coluna2: valor2}, ...]
- filename: "dados_nome.csv"

JSON (para dados):
- type: "json"
- data: {...} ou [{...}]
- filename: "dados_nome.json"

HTML (para páginas web):
- type: "html"
- title: "Título"
- content: "HTML ou texto"
- filename: "pagina_nome.html"

EXEMPLO PDF:
generate_file({
  type: "pdf",
  title: "Análise do Contrato Imperium",
  content: "## Resumo Executivo\n\nEste documento apresenta...\n\n## Cláusulas Principais\n\n- Cláusula 1: ...\n- Cláusula 2: ...",
  filename: "analise_contrato_imperium.pdf"
})

DICAS PARA PDF:
- Use markdown para formatação: # Título grande, ## Título médio, ### Título pequeno
- Use - ou * para listas
- Separe seções com linhas vazias
- O conteúdo será formatado automaticamente em páginas`,
        parameters: {
          type: 'object',
          properties: {
            type: {
              type: 'string',
              enum: ['csv', 'json', 'txt', 'html', 'markdown', 'pdf'],
              description: 'Tipo do arquivo: csv (planilhas/Excel), json (dados estruturados), txt (texto simples), html (relatório visual), markdown (documentação), pdf (documento formatado)'
            },
            data: {
              type: 'array',
              items: {
                type: 'object',
                additionalProperties: true
              },
              description: '⚠️ OBRIGATÓRIO para CSV/JSON! Array de objetos com TODOS os dados. Ex: [{data: "13/11/2024", preco_usd: 29100, variacao_24h: "+0.5%"}, {data: "12/11/2024", preco_usd: 28800, variacao_24h: "-1.0%"}]. Use chaves descritivas em português!'
            },
            content: {
              type: 'string',
              description: 'Conteúdo em texto completo para TXT/HTML/Markdown. Para CSV, use formato: "cabeçalho1,cabeçalho2\\nlinha1col1,linha1col2"'
            },
            title: {
              type: 'string',
              description: 'Título descritivo do documento (ex: "Histórico Bitcoin - Últimos 7 Dias")'
            },
            filename: {
              type: 'string',
              description: '⚠️ OBRIGATÓRIO! Nome DESCRITIVO com extensão. Ex: "bitcoin_historico_7dias.csv" (NÃO "dados.csv"). Sempre inclua a extensão .csv, .json, .html, etc'
            }
          },
          required: ['type', 'filename']
        }
      }
    };
  }

  /**
   * Gera PDF localmente no frontend usando jsPDF
   */
  private generatePDFLocal(title: string, content: string): string {
    console.log('📄 Gerando PDF localmente...');
    
    const doc = new jsPDF();
    
    // Configurações
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    
    // Título
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, yPosition);
    yPosition += 15;
    
    // Linha separadora
    doc.setDrawColor(102, 126, 234);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;
    
    // Conteúdo
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    const lines = content.split('\n');
    
    for (const line of lines) {
      // Títulos
      if (line.startsWith('## ')) {
        yPosition += 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const text = line.replace('## ', '');
        const splitText = doc.splitTextToSize(text, maxWidth);
        
        if (yPosition + (splitText.length * 7) > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.text(splitText, margin, yPosition);
        yPosition += splitText.length * 7 + 5;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
      } else if (line.startsWith('# ')) {
        yPosition += 5;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        const text = line.replace('# ', '');
        const splitText = doc.splitTextToSize(text, maxWidth);
        
        if (yPosition + (splitText.length * 8) > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.text(splitText, margin, yPosition);
        yPosition += splitText.length * 8 + 5;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
      } else if (line.startsWith('### ')) {
        yPosition += 3;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        const text = line.replace('### ', '');
        const splitText = doc.splitTextToSize(text, maxWidth);
        
        if (yPosition + (splitText.length * 6) > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.text(splitText, margin, yPosition);
        yPosition += splitText.length * 6 + 3;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        const text = line.replace(/^[*-] /, '• ');
        const splitText = doc.splitTextToSize(text, maxWidth - 5);
        
        if (yPosition + (splitText.length * 6) > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.text(splitText, margin + 5, yPosition);
        yPosition += splitText.length * 6;
      } else if (line.trim() === '') {
        yPosition += 4;
      } else {
        const splitText = doc.splitTextToSize(line, maxWidth);
        
        if (yPosition + (splitText.length * 6) > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.text(splitText, margin, yPosition);
        yPosition += splitText.length * 6;
      }
    }
    
    // Rodapé
    const totalPages = (doc as any).internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Gerado em: ${new Date().toLocaleString('pt-BR')} - Página ${i} de ${totalPages}`,
        margin,
        pageHeight - 10
      );
    }
    
    // Retornar como base64
    return doc.output('datauristring').split(',')[1];
  }

  /**
   * Executa a geração do arquivo
   */
  async generateFile(params: {
    type: string;
    data?: any[];
    content?: string;
    title?: string;
    filename?: string;
  }): Promise<GeneratedFile> {
    console.log('📄 Gerando arquivo:', params.type);

    // Se for PDF, gerar localmente (mais confiável)
    if (params.type === 'pdf') {
      try {
        console.log('📄 Gerando PDF localmente no frontend...');
        const title = params.title || 'Documento';
        const content = params.content || '';
        const filename = params.filename || `documento_${Date.now()}.pdf`;
        
        const base64Content = this.generatePDFLocal(title, content);
        const size = Math.round((base64Content.length * 3) / 4); // Estimar tamanho
        
        console.log('✅ PDF gerado localmente:', filename, `(${size} bytes)`);
        
        return {
          filename,
          content: base64Content,
          contentType: 'application/pdf',
          size
        };
      } catch (error) {
        console.error('❌ Erro ao gerar PDF localmente:', error);
        throw new Error('Não foi possível gerar o PDF. Tente novamente.');
      }
    }

    // Para outros formatos, tentar edge function
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL não configurado');
      }

      // Chamar Edge Function para gerar o arquivo
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        throw new Error(`Erro ao gerar arquivo: ${response.statusText}`);
      }

      const result = await response.json();

      console.log('✅ Arquivo gerado:', result.filename);

      return {
        filename: result.filename,
        content: result.content,
        contentType: result.contentType,
        size: result.size
      };

    } catch (error) {
      console.error('❌ Erro ao gerar arquivo:', error);
      throw error;
    }
  }

  /**
   * Cria um link de download para o arquivo gerado
   */
  createDownloadLink(file: GeneratedFile): string {
    try {
      // Decodificar base64
      const binaryString = atob(file.content);
      const bytes = new Uint8Array(binaryString.length);
      
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Criar Blob
      const blob = new Blob([bytes], { type: file.contentType });
      
      // Criar URL
      return URL.createObjectURL(blob);

    } catch (error) {
      console.error('Erro ao criar link de download:', error);
      throw error;
    }
  }

  /**
   * Faz download automático do arquivo
   */
  downloadFile(file: GeneratedFile) {
    try {
      const url = this.createDownloadLink(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✅ Download iniciado:', file.filename);
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      throw error;
    }
  }
}

export const fileGenerationService = new FileGenerationService();

